import React, { useState, useEffect } from "react";
import "./AddEquipment.css"; // Add styles for the layout
import "./Equipment.css"; // Equipment specific styles
import "./EquipmentList.css"; // Equipment list styles
import "../../shared/styles/snackbar.css";
import backIcon from "../../shared/assets/icons/back.png";
import uploadPlaceholder from "../../shared/assets/icons/upload.png";
import profileIcon from '../../shared/assets/icons/profile.png';
import bellIcon from '../../shared/assets/icons/bell.png';
import micIcon from '../../shared/assets/icons/mic.png';
import { useNavigate } from 'react-router-dom';
import SearchableDropdown from '../../components/SearchableDropdown';
import { equipmentService } from './services/equipmentService';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { getFirstErrorMessage } from '../../shared/utils/validator';
import HeaderNav from "../../shared/components/HeaderNav";
import { IoArrowUndoOutline } from "react-icons/io5";
import MobileFooterNav from "../../shared/components/MobileFooterNav";

const Equipment = () => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [showNotes, setShowNotes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [products, setProducts] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    product: '',
    make: '',
    model: '',
    serialNumber: '',
    location: '',
    details: ''
  });
  const [searchTerms, setSearchTerms] = useState({
    product: '',
    make: '',
    model: ''
  });

  const handleToggleNotes = () => {
    setShowNotes(!showNotes);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProductChange = async (productId) => {
    const selectedProduct = products.find(p => p.id == productId);
    setFormData(prev => ({ ...prev, product: productId, make: '', model: '' }));
    setSearchTerms(prev => ({ ...prev, product: selectedProduct?.name || '', make: '', model: '' }));
    setModels([]);
    
    // Load makes for selected product
    if (productId) {
      await loadMakes(productId);
    } else {
      setMakes([]);
    }
  };

  const handleMakeChange = async (makeId) => {
    const selectedMake = makes.find(m => m.id == makeId);
    setFormData(prev => ({ ...prev, make: makeId, model: '' }));
    setSearchTerms(prev => ({ ...prev, make: selectedMake?.make_name || '', model: '' }));
    
    // Load models for selected make
    if (makeId && formData.product) {
      await loadModels(makeId, formData.product);
    } else {
      setModels([]);
    }
  };

  const handleProductClear = () => {
    setFormData(prev => ({ ...prev, product: '', make: '', model: '' }));
    setSearchTerms(prev => ({ ...prev, make: '', model: '' }));
    setMakes([]);
    setModels([]);
  };

  const handleMakeClear = () => {
    setFormData(prev => ({ ...prev, make: '', model: '' }));
    setSearchTerms(prev => ({ ...prev, model: '' }));
    setModels([]);
  };

  const validateForm = () => {
    const requiredFields = ['name', 'product', 'make', 'model'];
    for (const field of requiredFields) {
      if (!formData[field] || !formData[field].toString().trim()) {
        showSnackbar('Please fill in all the required fields', 'error');
        return false;
      }
    }
    return true;
  };

  const navigate = useNavigate();

  const navigateToTaskList = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const equipmentData = {
        name: formData.name,
        serial_identifier: formData.name,
        category_id: formData.product,
        make_id: formData.make,
        model_id: formData.model,
        serial_number:formData.serialNumber
        // details: showNotes ? formData.details : "",
        // location_id: formData.serialNumber,
        // address: formData.location
      };
      
      const response = await equipmentService.createEquipment(equipmentData);
      
      if (response.success) {
        navigate('/equipmentlist');
      } else {
        const errorMessage = response?.errors ? getFirstErrorMessage(response) : ('An error occurred. Please try again.');
        showSnackbar(errorMessage, 'error');
      }
    } catch (error) {
      
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClientDetails = async () => {
    try {
      const data = await equipmentService.fetchClientDetails();
      setAddresses(data.address || []);
    } catch (error) {
      console.error('Error fetching client details:', error);
    }
  };

  const loadProducts = async (searchQuery = '') => {
    try {
      const response = await equipmentService.searchProducts(searchQuery);
      if (response.success && Array.isArray(response.data)) {
        const formattedProducts = response.data.map(item => ({
          id: item.id || item.product_id,
          name: item.name || item.product_name
        }));
        setProducts(formattedProducts);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  const loadMakes = async (productId, searchQuery = '') => {
    try {
      const response = await equipmentService.searchMakes(searchQuery, productId);
      if (response.success && Array.isArray(response.data)) {
        const formattedMakes = response.data.map(item => ({
          id: item.id || item.make_id,
          make_name: item.name || item.make_name
        }));
        setMakes(formattedMakes);
      }
    } catch (error) {
      console.error('Error loading makes:', error);
      setMakes([]);
    }
  };

  const loadModels = async (makeId, productId, searchQuery = '') => {
    try {
      const response = await equipmentService.searchModels(searchQuery, makeId, productId);
      if (response.success && Array.isArray(response.data)) {
        const formattedModels = response.data.map(item => ({
          id: item.id || item.model_id,
          model_name: item.name || item.model_name
        }));
        setModels(formattedModels);
      }
    } catch (error) {
      console.error('Error loading models:', error);
      setModels([]);
    }
  };

  useEffect(() => {
    loadProducts();
    fetchClientDetails();
  }, []);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setUploadedImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="maintenance-request">
   <HeaderNav profileIcon={profileIcon} />
      {/* Header Section */}
      
      <div className="second-header">
        <button className="back-button" >
           <IoArrowUndoOutline color="black" size={25} alt="Back" onClick={() => navigate('/equipmentlist')}/>
        </button>
        <h2 className="page-title">Add Equipment</h2>
      </div>
     {/* Mobile Header */}
         <header className="mobile-header">
           <div className="header-left">
              <IoArrowUndoOutline color="black" size={25} alt="Back" onClick={() => navigate('/dashboard')} />
           </div>
           <h1 className="app-title">Add Equipment</h1>
         </header>
      {/* Form Section */}
      <div className="form-container">
        <form className="maintenance-form" onSubmit={(e) => { e.preventDefault(); navigateToTaskList(); }}>
        <div className="form-group">
            <textarea
              id="name"
              // className="input-field name-textarea"
                 containerClassName="dropdown-container"
              className="dropdown-input"
              rows="1"
              placeholder="Name *"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            ></textarea>
          </div>
          <div className="form-group">
            <SearchableDropdown
              options={products}
              value={searchTerms.product}
              onChange={(term) => {
                setSearchTerms(prev => ({ ...prev, product: term }));
                  loadProducts(term);
              }}
              onSelect={(product) => handleProductChange(product.id)}
              onClear={handleProductClear}
              placeholder="Product *"
              // inputClassName="dropdown-input-style"
              containerClassName="dropdown-container"
              className="dropdown-input"
            />
            </div>
             <div className="form-group">
            <SearchableDropdown
              options={makes.map(make => ({ ...make, name: make.make_name }))}
              value={searchTerms.make}
              onChange={(term) => {
                setSearchTerms(prev => ({ ...prev, make: term }));
                if ( formData.product) {
                  loadMakes(formData.product, term);
                }
              }}
              onSelect={(make) => handleMakeChange(make.id)}
              onClear={handleMakeClear}
              placeholder="Make *"
              disabled={!formData.product}
              // inputClassName="dropdown-input-style"
                 containerClassName="dropdown-container"
              className="dropdown-input"
            />
            </div>
               <div className="form-group">
             <SearchableDropdown
              options={models.map(model => ({ ...model, name: model.model_name }))}
              value={searchTerms.model}
              onChange={(term) => {
                setSearchTerms(prev => ({ ...prev, model: term }));
                if ( formData.make && formData.product) {
                  loadModels(formData.make, formData.product, term);
                }
              }}
              onSelect={(model) => {
                const selectedModel = models.find(m => m.id == model.id);
                setFormData(prev => ({ ...prev, model: model.id }));
                setSearchTerms(prev => ({ ...prev, model: selectedModel?.model_name || '' }));
              }}
              onClear={() => {
                setFormData(prev => ({ ...prev, model: '' }));
                setSearchTerms(prev => ({ ...prev, model: '' }));
              }}
              placeholder="Model *"
              disabled={!formData.make || !formData.product}
              // inputClassName="dropdown-input-style"
                 containerClassName="dropdown-container"
              className="dropdown-input"
            />
          </div>
          <div className="form-group">
            <input
              id="serial-number"
              // className="input-field serial-textarea"
                 containerClassName="dropdown-container"
              className="dropdown-input"
              rows="1"
              placeholder="Serial Number "
              value={formData.serialNumber}
              onChange={(e) => handleInputChange('serialNumber', e.target.value)}
            />
          </div>

          {/* Image Upload Section */}
          {/* <div className="upload-section">
            <label className="upload-container">
              <div className="upload-placeholder">
                {uploadedImage ? (
                  <img
                    src={uploadedImage}
                    alt="Uploaded"
                    className="uploaded-image"
                  />
                ) : (
                  <>
                    <img
                      src={uploadPlaceholder}
                      alt="Upload"
                      className="upload-icon"
                    />
                    <p>Upload Picture</p>
                    <p>Upload or drop your file here</p>
                  </>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="file-input"
              />
            </label>
          </div> */}

          {/* Action Buttons */}
          {isLoading ?
          (  <div className="loading-container">
            <div className="circular-progress"></div>
          </div>):
          (
            <div className="form-actions">
            <button type="button" className="cancel-button" onClick={() => navigate('/equipmentlist')}>
              Cancel
            </button>
         
              <button type="submit" className="save-button">
                Save
              </button>
        
          </div>
          )
          }
        </form>
      </div>
              <MobileFooterNav />
    </div>
  );
};

export default Equipment;